import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { fileService } from '../../../services/fileService';
import { showToast } from '../../../stores/uiStore';
import { handleApiError } from '../../../services/api';
import { useSessionGate } from '../../../hooks/useSessionGate';

const NON_TERMINAL_SYNC_STATUS = ['pending', 'syncing'];

export const useFiles = (domainId: string) => {
  const { expired } = useSessionGate();
  return useQuery({
    queryKey: ['files', domainId],
    queryFn: () => fileService.getFiles(domainId),
    enabled: !!domainId && !expired,
    refetchInterval: (query) => {
      const files = query.state.data?.data?.files;
      if (files === undefined) return false;
      if (files.length === 0) return 5000;
      const hasNonTerminal = files.some(
        (f) => f.sync_status != null && NON_TERMINAL_SYNC_STATUS.includes(f.sync_status),
      );
      return hasNonTerminal ? 5000 : false;
    },
  });
};

export const useUploadFile = (domainId: string) => {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: ({
      file,
      onProgress,
    }: {
      file: File;
      onProgress?: (progress: number) => void;
    }) => fileService.uploadFile(domainId, file, onProgress),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['files', domainId] });
      queryClient.invalidateQueries({ queryKey: ['domain', domainId] });
      showToast.success('ファイルをアップロードしました');
    },
    onError: (error) => {
      const apiError = handleApiError(error);
      showToast.error(apiError.message);
    },
  });
};

export const useDeleteFile = (domainId: string) => {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: (fileId: string) => fileService.deleteFile(domainId, fileId),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['files', domainId] });
      queryClient.invalidateQueries({ queryKey: ['domain', domainId] });
      showToast.success('ファイルを削除しました');
    },
    onError: (error) => {
      const apiError = handleApiError(error);
      showToast.error(apiError.message);
    },
  });
};

